import * as React from 'react';
import * as _ from 'underscore';
import { Module } from '../../ts/Module';
import { BaseReactSettingsCtrl, BaseReactSettingsCtrlScope } from '../controllers/BaseReactSettingsCtrl';
import { ISiteSettingsForServerAdminFormScope } from '../controllers/SiteSettingsForServerAdminFormCtrl';
import { SectionContainerBaseProps } from '../general-settings/components/GeneralSettingsPageBase';
import { OuterSiteSettingsForServerAdminProps, SiteSettingsForServerAdminProps } from '../general-settings/components/ReactSiteSettingsForServerAdmin';
import { SettingsFormService } from './tbSettingsCoordinator';

class ReactSiteSettingsForServerAdminCtrl extends BaseReactSettingsCtrl<OuterSiteSettingsForServerAdminProps>{
  private static formName: string = 'ReactSiteSettingsForServerAdminForm';
  private static instanceCount = 0;
  private controllerId: string;

  public static $inject = [
    '$scope',
    'SettingsFormService',
  ];

  constructor(
    protected $scope: BaseReactSettingsCtrlScope<any> & SectionContainerBaseProps,
    formService: SettingsFormService
  ) {
    super($scope, formService, ReactSiteSettingsForServerAdminCtrl.formName + ReactSiteSettingsForServerAdminCtrl.instanceCount++);
    this.controllerId = ReactSiteSettingsForServerAdminCtrl.formName + ReactSiteSettingsForServerAdminCtrl.instanceCount++;
  }

  $onInit() {
    // This directive uses an isolated scope to bind the value of sections and allow multiple copies of it to be created
    // use the parent scope to access values defined in the parent controller
    const $parent = this.$scope.$parent as ISiteSettingsForServerAdminFormScope;
    const {...siteInfo} = $parent.siteSettingsForServerAdmin;
    this.$scope.props = {
      ...this.$scope.props,
      settings: {...this.$scope.settings},
      siteInfo,

      // copy overrides
      hideNameAndId: $parent.hideNameAndId,
      hideAvailability: $parent.hideAvailability,
      hideRevisionHistoryLimitWarning: $parent.hideRevisionHistoryLimitWarning,
      availability: $parent.siteSettingsForServerAdmin.availability,
      isDefaultSite: $parent.isDefaultSite,
      sections: this.$scope.sections,

      // copy changed values to the parent scope and trigger a digest
      mergeIntoParentScope: (values: SiteSettingsForServerAdminProps) => {
        $parent.siteSettingsForServerAdmin.settings = values.settings;
        $parent.siteSettingsForServerAdmin.availability = values.availability;

        this.$scope.$applyAsync();
      }
    };

    // watch changes to the settings to rerender the react component if they change. This is used to provide updated
    // values of the non-migrated settings
    // $parent.$watch('siteSettingsForServerAdmin.settings', (newValue: any) => {
    //   this.$scope.props = {
    //     ...this.$scope.props,
    //     settings: {...newValue}
    //   };
    // }, true);
    // watch changes to the settings to rerender the react component if they change. This is used to provide updated
    // values of the non-migrated settings
    $parent.$on('settings.change', (event: any, newValue: any) => {
      this.$scope.props = {
        ...this.$scope.props,
        settings: newValue
      };
    });

    $parent.$on('availability.change', (event: any, newValue: string) => {
      this.$scope.props = {
        ...this.$scope.props,
        availability: newValue
      };
    });
  }
}

/**
 * An adapter to create named settings sections in angular pug files. This simplifies adding new settings using react
 * without worrying about the details of adapting it to angularjs.
 *
 * Usage: just add the directive in siteSettingsForServerAdminForm.pug as follows:
 * tb:-site-settings-for-server-admin-form-adapter(sections="['section1', 'section2', ...]")
 *
 * where section1, section2 are the names of the actual sections to render in the adapter.
 *
 * This adapter can render multiple sections, so if there is an existing adapter in the area you want to add your section
 * you can append your section name to the array without having to add a new adapter.
 */
Module.instance().directive('tbSiteSettingsForServerAdminFormAdapter', () => {
  return {
    restrict: 'E',
    require: ['^?tbSettingsCoordinator', '^tbSiteSettingsForServerAdminForm'],
    template: '<tb:react-site-settings-for-server-admin props="props"/>',
    controller: ReactSiteSettingsForServerAdminCtrl,
    scope: {
      sections: '<',
      settings: '<'
    }
  };
});

export type ErrorValidator<T extends {settings: object}> = (props: T) => string;
export type TouchCalculator<T extends {settings: object}> = (props: T) => boolean;

export type ComponentSpecs<Props extends {settings: object}> = {
  name: string;
  component: React.ComponentType<Props>;
  meta?: {
    [k in keyof Props['settings']]?: {
      initError: ErrorValidator<Props>;
      initTouched: TouchCalculator<Props>;
    }
  };
};

/**
 * A helper function, used as a workaround to get stronger types when passing specs to createComponentRegistry
 * @param specs
 */
export function componentRegistryEntry<Props extends {settings: object}>(specs: ComponentSpecs<Props>) {
  return specs;
}

export function createComponentRegistry(...specArray: ComponentSpecs<any>[]) {
  const result: _.Dictionary<ComponentSpecs<any>> = {};
  specArray.forEach(spec => result[spec.name] = spec);
  return result;
}
